import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import TanStackTable from '../../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import Chip from '../../../components/Chip';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaEye } from 'react-icons/fa';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDeleteTip, useGetTipsList } from "../../../queries/knowledgeBoard";
import { ActionButton } from '../../../components/Forms/ActionButton';
import AttachedProductImpactModal from '../../../components/AttachedProductImpactModal';
const EditIcon = require("../../../utils/media/icons/edit_icon.png");

const ViewTipsList = () => {

    const navigate = useNavigate();
    const { data, isPending } = useGetTipsList();
    const { mutateAsync: deleteData, isPending: isDeleting } = useDeleteTip();

    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
        status: '',
        title: '',
    });
    const [showImpactModal, setShowImpactModal] = useState(false);
    const [selectedTip, setSelectedTip] = useState(null);

    useEffect(() => {
        if (data) {
            setFilteredData(data);
        }
    }, [data]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        let filtered = data || [];

        if (filters.status) {
            const isActive = filters.status === 'active';
            filtered = filtered.filter((tip) => tip.isActive === isActive);
        }

        if (filters.title) {
            filtered = filtered.filter((tip) =>
                tip.title.toLowerCase().includes(filters.title.toLowerCase())
            );
        }

        setFilteredData(filtered);
    };

    const handleDelete = useCallback(async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this tip?");
        if (!confirm) return;
        if (isDeleting) return;
        try {
            await deleteData(id, {
                onSuccess: () => {
                    toast.success("Tip deleted successfully");
                },
                onError: () => {
                    toast.error("Failed to delete tip");
                }
            });
        } catch (error) {
            toast.error("Failed to delete tip");
        }
    }, [deleteData, isDeleting]);

    const handleViewImpact = useCallback((tip) => {
        setSelectedTip(tip);
        setShowImpactModal(true);
    }, []);

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('title', {
            header: 'Title',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('description', {
            header: 'Description',
            cell: info => (
                <div
                    className="line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: info.getValue() || 'N/A' }}
                />
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created At',
            align: 'center',
            cell: info => (
                <div className='text-center'>
                    {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                    <br />
                    <small className='text-secondary'>
                        {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </small>
                </div>
            ),
        }),
        columnHelper.accessor('isActive', {
            header: 'Status',
            align: 'center',
            cell: info => <Chip value={info.getValue() ? 'Active' : 'Inactive'} color={info.getValue() ? 'green' : 'inactive'} />,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            enableColumnFilter: false,
            align: 'center',
            cell: info => (
                <div className='flex items-center justify-center'>
                    {info.row.original?.attachedProduct && (
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleViewImpact(info.row.original)}
                        >
                            <FaEye className="text-chipPrimary" />
                        </button>
                    )}
                    <button
                        className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                        onClick={() => navigate('/knowledge-board/tips/create-tips', { state: { id: info.row.original?._id } })}
                    >
                        <img src={EditIcon} alt="edit" />
                    </button>
                    <button
                        className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                        onClick={() => handleDelete(info.row.original._id)}
                    >
                        <FaTrash className="fill-chipRed" />
                    </button>
                </div>
            ),
        }),
    ], [navigate, handleDelete, handleViewImpact]);

    if (isPending) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of Tips</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] "
                onSubmit={handleSearch}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                    <ActionButton
                        variant={'reset'}
                        text={'Create New Tip'}
                        handleSubmit={() => navigate('/knowledge-board/tips/create-tips')}
                    />
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full md:w-1/2'>
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className="flex-1 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full md:w-1/2'>
                        <div className="flex-col w-full">
                            <label htmlFor="title" className="text-gray-900 text-base">
                                Tip Title
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full bg-white rounded-md border border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="title"
                                    value={filters.title}
                                    onChange={handleFilterChange}
                                    placeholder="Enter Tip Title"
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={() => {
                            setFilters({
                                status: '',
                                title: '',
                            });
                            setFilteredData(data || []);
                        }}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={handleSearch}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
                    <h1 className="self-start text-lg font-bold pb-4">List of Tips</h1>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No tips available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={3000}
            />

            <AttachedProductImpactModal
                isOpen={showImpactModal}
                onClose={() => {
                    setShowImpactModal(false);
                    setSelectedTip(null);
                }}
                item={selectedTip}
                location="tip-details"
                contentType="tip"
            />
        </main>
    );
}

export default ViewTipsList;
